import { apiFetch } from "../../api/client";
import type {
  AdminAudienceFilterPreviewParams,
  AdminAudienceFilterSelection,
  AdminCampaignBatchItem,
  AdminCampaignBatchRequest,
  AdminCampaignDetail,
  AdminCampaignDraft,
  AdminCampaignDraftWithoutSegment,
  AdminCampaignListParams,
  AdminCampaignMonitoring,
  AdminCampaignDispatchAcknowledgement,
  AdminCampaignPage,
  AdminContactPage,
  ScheduleCampaignBatchResult,
  ScheduleCampaignRequest,
} from "../../api/types";

function buildQueryString(params: AdminCampaignListParams): string {
  const search = new URLSearchParams();
  if (params.estado) search.set("estado", params.estado);
  const q = params.q?.trim();
  if (q) search.set("q", q);
  search.set("page", String(params.page ?? 1));
  search.set("size", String(params.size ?? 20));
  return search.toString();
}

/** `GET admin/campaigns?...` (role Consulta / Operador). */
export function listCampaigns(params: AdminCampaignListParams = {}): Promise<AdminCampaignPage> {
  return apiFetch<AdminCampaignPage>(`/panel/campaigns?${buildQueryString(params)}`);
}

/** `GET admin/campaigns/{id}` (role Consulta / Operador). */
export function getCampaign(id: string): Promise<AdminCampaignDetail> {
  return apiFetch<AdminCampaignDetail>(`/panel/campaigns/${id}`);
}

/** `POST admin/campaigns` (role Operador). `nombre`/`asunto` travel as two independent fields. */
export function createCampaign(draft: AdminCampaignDraft): Promise<{ campaignId: string }> {
  return apiFetch<{ campaignId: string }>("/panel/campaigns", {
    method: "POST",
    body: JSON.stringify(draft),
  });
}

/**
 * epic #236 tarea #241 (nuevo flujo de campaña sin Segmentos): mismo endpoint que
 * `createCampaign` -- la ausencia de `segmentId` en el body es la señal que
 * `AdminCampaignsFunction.Create` usa para auto-provisionar el segmento placeholder.
 */
export function createCampaignWithoutSegment(draft: AdminCampaignDraftWithoutSegment): Promise<{ campaignId: string }> {
  return apiFetch<{ campaignId: string }>("/panel/campaigns", {
    method: "POST",
    body: JSON.stringify(draft),
  });
}

/**
 * feat/campaign-batch-scheduling: `POST panel/campaigns/batch` (role
 * Operador) — reparte la audiencia que matchea `criterio` en N campañas de
 * tamaño fijo, cada una ya 'programada'. Devuelve la lista de lotes creados
 * (posiblemente vacía si 0 contactos matchean — no es un error).
 */
export function createCampaignBatch(request: AdminCampaignBatchRequest): Promise<AdminCampaignBatchItem[]> {
  return apiFetch<AdminCampaignBatchItem[]>("/panel/campaigns/batch", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** `PUT admin/campaigns/{id}` (role Operador). */
export function updateCampaign(id: string, draft: AdminCampaignDraft): Promise<void> {
  return apiFetch<void>(`/panel/campaigns/${id}`, {
    method: "PUT",
    body: JSON.stringify(draft),
  });
}

/** `POST admin/campaigns/{id}/schedule` (role Operador). */
export function scheduleCampaign(id: string, body: ScheduleCampaignRequest): Promise<void> {
  return apiFetch<void>(`/panel/campaigns/${id}/schedule`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * `POST panel/campaigns/batch/{batchId}/schedule` (role Operador) —
 * odd/tasks/campaign-batch-bulk-schedule.md task 1/2: schedules every
 * `'borrador'` sibling sharing `batchId` in one call, reusing each
 * campaign's own already-computed `fechaProgramada` (no request body). 400
 * for an invalid `batchId`, 404 (`ApiError`) if the batch has zero
 * campaigns.
 */
export function scheduleCampaignBatch(batchId: string): Promise<ScheduleCampaignBatchResult> {
  return apiFetch<ScheduleCampaignBatchResult>(`/panel/campaigns/batch/${batchId}/schedule`, { method: "POST" });
}

/** `POST admin/campaigns/{id}/cancel` (role Operador). */
export function cancelCampaign(id: string): Promise<void> {
  return apiFetch<void>(`/panel/campaigns/${id}/cancel`, { method: "POST" });
}

/**
 * `POST admin/campaigns/{id}/dispatch` (role Operador). async-campaign-dispatch (Option A,
 * native review correction PR #311): fire-and-forget — the endpoint enqueues the dispatch and
 * returns 202 with just `{ campaignId }` immediately; `CampaignDispatchService`'s per-contact
 * loop now runs in the background `CampaignDispatchQueueFunction` consumer, not inline here.
 */
export function dispatchCampaign(id: string): Promise<AdminCampaignDispatchAcknowledgement> {
  return apiFetch<AdminCampaignDispatchAcknowledgement>(`/panel/campaigns/${id}/dispatch`, { method: "POST" });
}

/** `GET admin/campaigns/{id}/monitoring` (role Consulta / Operador — live counts). */
export function getCampaignMonitoring(id: string): Promise<AdminCampaignMonitoring> {
  return apiFetch<AdminCampaignMonitoring>(`/panel/campaigns/${id}/monitoring`);
}

/**
 * `POST panel/campaign-audience-preview` (role Consulta) — paginated preview of contacts
 * matching the 4 combinable audience-filter criteria. Response is the same `AdminContactPage`
 * envelope as `GET admin/contacts`, no separate type.
 *
 * odd/tasks/campaign-audience-empresa-multiselect.md (F2/F4, Codex P2 on slice 2 review): this
 * used to be `GET ...?empresas=A&empresas=B&...` — a repeated query param per selected company.
 * 100 companies x ~200 chars in a GET query string exceeds the 8 KB request-line limit most
 * servers/proxies enforce, so this now POSTs the exact same criteria as a JSON body instead.
 * The backend kept the GET form for compatibility (AdminAudienceFilterFunction.Preview), but
 * this panel always uses the POST form going forward. `empresas` travels as `null` (never an
 * empty array) when no company is selected, matching every other optional field here.
 */
export function getCampaignAudiencePreview(params: AdminAudienceFilterPreviewParams = {}): Promise<AdminContactPage> {
  return apiFetch<AdminContactPage>("/panel/campaign-audience-preview", {
    method: "POST",
    body: JSON.stringify({
      showId: params.showId ?? null,
      paisEmpresa: params.paisEmpresa ?? null,
      empresas: params.empresas && params.empresas.length > 0 ? params.empresas : null,
      cargo: params.cargo ?? null,
      categoria: params.categoria ?? null,
      page: params.page ?? 1,
      size: params.size ?? 20,
    }),
  });
}

/** `PUT panel/campaigns/{id}/audience-filter` (role Operador). 404 if the campaign is missing/foreign, 409 if it is no longer in `borrador`. */
export function saveAudienceFilter(id: string, selection: AdminAudienceFilterSelection): Promise<void> {
  return apiFetch<void>(`/panel/campaigns/${id}/audience-filter`, {
    method: "PUT",
    body: JSON.stringify(selection),
  });
}

/** `GET panel/campaigns/{id}/audience-filter` (role Consulta). 404 (`ApiError.kind === "notFound"`) when no filter has been saved yet — callers must treat that as "no filter", not as an error state. */
export function getAudienceFilter(id: string): Promise<AdminAudienceFilterSelection> {
  return apiFetch<AdminAudienceFilterSelection>(`/panel/campaigns/${id}/audience-filter`);
}

/**
 * `GET panel/empresa-options` (role Consulta) — bounded (`size<=50` per
 * page) selector read for the audience-filter editors' Empresa selector
 * (campaign-audience-filter-empresa, frontend item 9), same "options"
 * pattern as `getShowOptions` — plain distinct company names, not
 * `{id, nombre}` objects (there is no separate `empresa` entity/id).
 *
 * `showId` (issue #350): optional narrowing filter — when present, only
 * company names belonging to contacts linked to that Show (backend JOINs
 * `contact_show`).
 *
 * `q` (issue #350, odd/tasks/empresa-options-search-by-show.md): optional
 * case-insensitive substring search over ALL company names server-side
 * (`ILIKE`, not just the currently loaded page) — `EmpresaSelector` debounces
 * this before calling. Both omitted preserves the original
 * unfiltered/first-page-alphabetical behavior.
 *
 * `exact` (T4, odd/tasks/empresa-options-search-by-show.md): optional EXACT
 * existence check (equality, never `ILIKE`) — used only by `EmpresaSelector`'s
 * Show-change validation, never by the visible search-as-you-type listbox.
 * Unlike `q` (bounded to the page `size`), `exact` always returns at most one
 * name, so it can never miss a valid Empresa hiding beyond the first page of
 * substring matches. When both `exact` and `q` are given, the backend lets
 * `exact` win (see `AdminContactSqlBuilder.BuildEmpresaOptionsQuery`) — this
 * client never sends both at once regardless.
 */
export interface EmpresaOptionsPageResponse {
  items: string[];
  hasMore: boolean;
  offset: number;
  size: number;
}

/**
 * Paged variant of `getEmpresaOptions` (panel-feedback-round2 T2): sending `offset` opts in to
 * the backend's `{items, hasMore, offset, size}` envelope, so every company is reachable by
 * loading successive pages (search ranking is preserved across pages server-side).
 */
export function getEmpresaOptionsPage(params: {
  showId?: string;
  q?: string;
  offset: number;
  size?: number;
}): Promise<EmpresaOptionsPageResponse> {
  const search = new URLSearchParams();
  if (params.showId) search.set("showId", params.showId);
  if (params.q) search.set("q", params.q);
  if (params.size) search.set("size", String(params.size));
  search.set("offset", String(params.offset));
  return apiFetch<EmpresaOptionsPageResponse>(`/panel/empresa-options?${search.toString()}`);
}

export function getEmpresaOptions(params: { showId?: string; q?: string; exact?: string } = {}): Promise<string[]> {
  const search = new URLSearchParams();
  if (params.showId) search.set("showId", params.showId);
  if (params.q) search.set("q", params.q);
  if (params.exact) search.set("exact", params.exact);
  const queryString = search.toString();
  return apiFetch<string[]>(`/panel/empresa-options${queryString ? `?${queryString}` : ""}`);
}
